/**
 * Ambassadors Program API Client
 */

import { apiClient } from './client';

export interface AmbassadorStatus {
  enrolled: boolean;
  applicationStatus?: 'pending' | 'rejected';
  ambassador: Ambassador | null;
  rank: number | null;
  totalActive?: number;
  eligibility: {
    eligible: boolean;
    requirements: EligibilityRequirement[];
  } | null;
}

export interface Ambassador {
  id: string;
  user_id: string;
  wallet_address: string;
  score: number;
  rank: number | null;
  status: 'pending' | 'active' | 'suspended' | 'banned' | 'rejected';
  joined_at: string;
  monthly_mints_count: number;
  total_free_mints: number;
  deals_balance: number;
  deals_bonus: number;
  username?: string;
  avatar_url?: string;
}

export interface EligibilityRequirement {
  key: string;
  label: string;
  met: boolean;
  current?: string;
  required?: string;
  orGroup?: string;
  actionUrl?: string;
  actionLabel?: string;
}

export interface LeaderboardResponse {
  ambassadors: Ambassador[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  userPosition: {
    rank: number;
    score: number;
    wallet_address: string;
  } | null;
}

export interface AmbassadorAllocation {
  id: string;
  launch_id: string;
  launch_name: string;
  launch_slug: string;
  launch_thumbnail?: string;
  wallet_address: string;
  selection_type: 'guaranteed' | 'random';
  status: 'selected' | 'claimed' | 'expired';
  claimed_at: string | null;
  expires_at: string | null;
  lock_expires_at: string | null;
  x_post_url: string | null;
  x_post_submitted_at: string | null;
  x_post_deadline_at: string | null;
  x_post_missed: boolean;
  x_post_creator_rating: number | null;
  x_post_creator_rated_at: string | null;
  violation_detected_at: string | null;
  violation_type: string | null;
  nft_token_id: string | null;
}

export interface AmbassadorDashboardStats {
  total_members: number;
  new_this_month: number;
  active_this_month: number;
  free_mints_this_month: number;
  free_mints_all_time: number;
  pending_violations: number;
  pending_applications: number;
  average_score: number;
}

export interface AmbassadorConfig {
  hold_period_days: number;
  monthly_cap: number;
  max_slots_pct: number;
  max_slots_absolute: number;
  claim_deadline_days: number;
  x_post_deadline_hours: number;
  min_mint_volume_xrp: number;
  x_post_miss_penalty_pct: number;
  points_per_xrp_minted: number;
  creator_rating_1_pct: number;
  creator_rating_2_pct: number;
  creator_rating_4_pct: number;
  creator_rating_5_pct: number;
  deals_holding_multiplier: number;
  deals_holding_cap: number;
  deals_holding_power: number;
  deals_holding_reference: number;
  deals_holding_enabled: number;
}

// Public endpoints
export async function getAmbassadorStatus() {
  return apiClient.get<{ success: boolean; data: AmbassadorStatus }>('/ambassadors/status');
}

export async function applyAmbassadorProgram() {
  return apiClient.post<{ success: boolean; data: Ambassador }>('/ambassadors/apply');
}

/** @deprecated Use applyAmbassadorProgram instead */
export async function joinAmbassadorProgram() {
  return applyAmbassadorProgram();
}

export async function leaveAmbassadorProgram() {
  return apiClient.post<{ success: boolean }>('/ambassadors/leave');
}

export async function getLeaderboard(page = 1, limit = 50) {
  return apiClient.get<{ success: boolean; data: LeaderboardResponse }>(
    `/ambassadors/leaderboard?page=${page}&limit=${limit}`
  );
}

export async function getMyAllocations(page = 1, limit = 20) {
  return apiClient.get<{ success: boolean; data: { allocations: AmbassadorAllocation[]; total: number } }>(
    `/ambassadors/my-allocations?page=${page}&limit=${limit}`
  );
}

export async function getMyActiveAllocations() {
  return apiClient.get<{ success: boolean; data: AmbassadorAllocation[] }>('/ambassadors/my-active');
}

export async function submitXPost(allocationId: string, url: string) {
  return apiClient.post<{ success: boolean }>(`/ambassadors/allocations/${allocationId}/x-post`, { url });
}

// Creator endpoints
export interface LaunchAmbassadorData {
  ambassadorSlots: number;
  selectionCompleted: boolean;
  allocations: (AmbassadorAllocation & {
    score?: number;
    username?: string;
    avatar_url?: string;
    x_account?: string;
  })[];
  summary: {
    totalAllocated: number;
    totalClaimed: number;
    xPostsSubmitted: number;
    xPostsMissed: number;
  };
}

export async function getLaunchAmbassadors(launchId: string) {
  return apiClient.get<{ success: boolean; data: LaunchAmbassadorData }>(
    `/launches/${launchId}/ambassadors`
  );
}

export async function rateAmbassadorXPost(launchId: string, allocationId: string, rating: number) {
  return apiClient.post<{ success: boolean; data: { rating: number } }>(
    `/launches/${launchId}/ambassadors/allocations/${allocationId}/rating`,
    { rating }
  );
}

export async function updateLaunchAmbassadorConfig(launchId: string, ambassadorSlots: number) {
  return apiClient.patch<{ success: boolean; data: { ambassador_slots: number } }>(
    `/launches/${launchId}/ambassadors/config`,
    { ambassador_slots: ambassadorSlots }
  );
}

// Admin endpoints
export async function getAdminDashboard() {
  return apiClient.get<{ success: boolean; data: AmbassadorDashboardStats }>('/admin/ambassadors/dashboard');
}

export async function getAdminMembers(params: {
  page?: number; limit?: number;
  status?: string; search?: string;
  hasViolations?: boolean;
} = {}) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.status) searchParams.set('status', params.status);
  if (params.search) searchParams.set('search', params.search);
  if (params.hasViolations !== undefined) searchParams.set('hasViolations', String(params.hasViolations));
  return apiClient.get<{ success: boolean; data: { members: any[]; total: number; page: number; totalPages: number } }>(
    `/admin/ambassadors/members?${searchParams}`
  );
}

export async function getAdminMemberDetail(id: string) {
  return apiClient.get<{ success: boolean; data: any }>(`/admin/ambassadors/members/${id}`);
}

export async function adjustScore(id: string, adjustment: number, reason: string) {
  return apiClient.post<{ success: boolean; data: Ambassador }>(
    `/admin/ambassadors/members/${id}/adjust-score`,
    { adjustment, reason }
  );
}

export async function suspendMember(id: string, reason: string) {
  return apiClient.post<{ success: boolean }>(`/admin/ambassadors/members/${id}/suspend`, { reason });
}

export async function banMember(id: string, reason: string) {
  return apiClient.post<{ success: boolean }>(`/admin/ambassadors/members/${id}/ban`, { reason });
}

export async function reinstateMember(id: string, reason: string) {
  return apiClient.post<{ success: boolean }>(`/admin/ambassadors/members/${id}/reinstate`, { reason });
}

export async function resetMonthlyCounter(id: string, reason: string) {
  return apiClient.post<{ success: boolean }>(`/admin/ambassadors/members/${id}/reset-monthly`, { reason });
}

export async function deleteMember(id: string) {
  return apiClient.delete<{ success: boolean }>(`/admin/ambassadors/members/${id}`);
}

export async function getAdminAllocations(page = 1, limit = 50) {
  return apiClient.get<{ success: boolean; data: any }>(
    `/admin/ambassadors/allocations?page=${page}&limit=${limit}`
  );
}

export async function getAdminViolations(params: { page?: number; type?: string; status?: string } = {}) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.type) searchParams.set('type', params.type);
  if (params.status) searchParams.set('status', params.status);
  return apiClient.get<{ success: boolean; data: any }>(`/admin/ambassadors/violations?${searchParams}`);
}

export async function reverseViolation(id: string, reason: string) {
  return apiClient.post<{ success: boolean }>(`/admin/ambassadors/violations/${id}/reverse`, { reason });
}

export async function getAdminConfig() {
  return apiClient.get<{ success: boolean; data: AmbassadorConfig }>('/admin/ambassadors/config');
}

export async function updateAdminConfig(updates: Partial<AmbassadorConfig>) {
  return apiClient.patch<{ success: boolean; data: AmbassadorConfig }>('/admin/ambassadors/config', updates);
}

// Admin application endpoints
export async function getAdminApplications(params: { page?: number; search?: string } = {}) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.search) searchParams.set('search', params.search);
  return apiClient.get<{ success: boolean; data: { applications: any[]; total: number; page: number; totalPages: number } }>(
    `/admin/ambassadors/applications?${searchParams}`
  );
}

export async function getAdminApplicationDetail(id: string) {
  return apiClient.get<{ success: boolean; data: any }>(`/admin/ambassadors/applications/${id}`);
}

export async function approveApplication(id: string) {
  return apiClient.post<{ success: boolean; data: Ambassador }>(`/admin/ambassadors/applications/${id}/approve`);
}

export async function rejectApplication(id: string, reason: string) {
  return apiClient.post<{ success: boolean }>(`/admin/ambassadors/applications/${id}/reject`, { reason });
}
